import type {
  AccessRecordPublic,
  AccessRequestPublic,
  AccessRequestStatus,
  UserPublic,
} from '@accounts/shared';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { STR } from '../../../i18n';
import {
  ApiError,
  listAccessRecords,
  listAccessRequests,
  listGrants,
  listUsers,
  removeAccessRecord,
  updateAccessRecord,
} from '../../../lib/api';
import { formatDate } from '../../../lib/format';
import { queryKeys } from '../../../lib/queryKeys';
import { surfaceById } from '../../../lib/surfaces';
import { showToast } from '../../../lib/toast';
import { Card, CardHeader, Chip, EmptyRow } from '../../../ui/Primitives';
import { eyebrowStyle, monoStyle, quietButtonStyle, rowActionStyle } from '../../../ui/styles';

import {
  passwordHandlingLabel,
  RECORD_STATUS_TONE,
  REQUEST_STATUS_TONE,
  recordStatusLabel,
  requestStatusLabel,
} from './accessFormat';
import { type AppStatusKey, appStatusAt, indexAppStatus } from './appStatus';
import { useApplicationsList } from './catalog/queries';
import { AddRecordDialog, RecordDialog } from './RecordDialogs';
import { OnboardingChecklist } from '../onboarding/OnboardingChecklist';

/**
 * THE PERSON PAGE'S ACCESS SECTION (spec row 4, FINAL demo R3-2 person page) — ADDED below
 * the existing cards, never in place of them: a line of facts, Access at a glance (the small
 * matrix), then Line by line and Requests, both folded. The existing Application access
 * card stays the home of suite-app grants; this section draws EVERY registered suite app
 * with this person's state on it (`appStatus.ts`: granted, requested, revoked or not
 * granted), for administrators only — `/api/admin/grants` and the applications list are
 * admin-gated, so for HR the line is not drawn.
 *
 * Outside-system access is ACCESS RECORDS. Every write here is an affordance over the
 * server, which enforces HR's narrower scope and refuses with its own words.
 */

const LIST_LIMIT = 200;

/** `GET /api/admin/access/requests` takes ONE status; the person's whole history is all four. */
const REQUEST_STATUSES: readonly AccessRequestStatus[] = [
  'pending',
  'approved',
  'denied',
  'cancelled',
];

type Dialog = { kind: 'record'; record: AccessRecordPublic; edit: boolean } | { kind: 'add' };

interface Group {
  key: string;
  name: string;
  records: AccessRecordPublic[];
}

/** One group per top-level node; rows with no place sit in a "Not filed" group, last. */
function groupByTop(records: readonly AccessRecordPublic[]): Group[] {
  const groups = new Map<string, Group>();
  for (const record of records) {
    const top = record.path[0];
    const key = top?.id ?? '';
    const name = top?.name ?? STR.accessCommon.notPlaced;
    const group = groups.get(key) ?? { key, name, records: [] };
    group.records.push(record);
    groups.set(key, group);
  }
  return [...groups.values()].sort((a, b) => {
    if (a.key === '') return 1;
    if (b.key === '') return -1;
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  });
}

/** A row's place below its top-level group, then its own name. */
function shortName(record: AccessRecordPublic): string {
  const rest = record.path.slice(1).map((step) => step.name);
  return [...rest, record.catalogItem.name].join(STR.accessCommon.pathSeparator);
}

function pillTitle(record: AccessRecordPublic): string {
  const parts = [
    `${record.catalogItem.name}: ${recordStatusLabel(record.status)}`,
    record.login ?? STR.accessCommon.noLogin,
  ];
  if (record.sharedWith.length > 0) parts.push(STR.access.person.sharedWith(record.sharedWith.length));
  if (record.login !== null) {
    parts.push(STR.access.person.passwordLine(passwordHandlingLabel(record.passwordHandling)));
  }
  return parts.join(', ');
}

/** A section card that folds, as the Applications page's "Launcher tile" section does. */
function FoldCard({
  title,
  count,
  aside,
  children,
}: {
  title: string;
  count: number;
  aside: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
        }}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '13px 18px',
          border: 'none',
          borderBottom: open ? '1px solid var(--border)' : 'none',
          background: 'var(--surface-2)',
          color: 'var(--text)',
          fontSize: 13.5,
          fontWeight: 650,
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <span>
          {title}{' '}
          <span className="tx-subtle" style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {count}
          </span>
        </span>
        <span className="tx-subtle" style={{ fontSize: 12, fontWeight: 500 }}>
          {aside}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 650, color: 'var(--accent)' }}>
          {open ? STR.access.person.collapse : STR.access.person.expand}
        </span>
      </button>
      {open ? children : null}
    </div>
  );
}

function StatusPill({
  record,
  text,
  onOpen,
}: {
  record: AccessRecordPublic;
  text: string;
  onOpen: (record: AccessRecordPublic) => void;
}) {
  const title = pillTitle(record);
  return (
    <button
      type="button"
      className="tbtn"
      title={title}
      aria-label={title}
      onClick={() => {
        onOpen(record);
      }}
      style={{ padding: 1, border: 'none', borderRadius: 999, background: 'none', lineHeight: 1 }}
    >
      <Chip tone={RECORD_STATUS_TONE[record.status]} size="sm">
        {text}
      </Chip>
    </button>
  );
}

/**
 * The small matrix: a row per top-level node, a column per second-level node this person
 * has anywhere, plus a column for rows filed on the top-level node itself.
 */
function GlanceGrid({
  groups,
  onOpen,
}: {
  groups: readonly Group[];
  onOpen: (record: AccessRecordPublic) => void;
}) {
  const all = groups.flatMap((group) => group.records);
  const columns = new Map<string, string>();
  for (const record of all) {
    const second = record.path[1];
    if (second !== undefined) columns.set(second.id, second.name);
  }
  const sortedColumns = [...columns.entries()].sort((a, b) =>
    a[1].toLowerCase().localeCompare(b[1].toLowerCase()),
  );
  const onTop = all.some((record) => record.path.length < 2);
  const topLevelName = all.find((record) => record.path[0] !== undefined)?.path[0]?.level.name;

  function cell(records: readonly AccessRecordPublic[], label: (r: AccessRecordPublic) => string) {
    if (records.length === 0) return <span className="tx-faint">·</span>;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {records.map((record) => (
          <StatusPill key={record.id} record={record} text={label(record)} onOpen={onOpen} />
        ))}
      </div>
    );
  }

  return (
    <div className="dscroll">
      <table className="dtable" style={{ width: 'auto', minWidth: '100%' }}>
        <thead>
          <tr>
            <th style={{ minWidth: 130 }}>{topLevelName ?? STR.access.person.glanceGroupColumn}</th>
            {sortedColumns.map(([id, name]) => (
              <th
                key={id}
                style={{ textTransform: 'none', letterSpacing: 0, fontSize: 11.5, color: 'var(--text)' }}
              >
                {name}
              </th>
            ))}
            {onTop ? (
              <th style={{ textTransform: 'none', letterSpacing: 0, fontSize: 11.5 }}>
                {STR.access.person.glanceOnGroup(topLevelName ?? STR.access.person.glanceGroupColumn)}
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <tr key={group.key}>
              <td style={{ fontWeight: 650, whiteSpace: 'nowrap' }}>{group.name}</td>
              {sortedColumns.map(([id]) => (
                <td key={id}>
                  {cell(
                    group.records.filter((record) => record.path[1]?.id === id),
                    (record) =>
                      [...record.path.slice(2).map((step) => step.name), record.catalogItem.name].join(
                        STR.accessCommon.pathSeparator,
                      ),
                  )}
                </td>
              ))}
              {onTop ? (
                <td>
                  {cell(
                    group.records.filter((record) => record.path.length < 2),
                    (record) => record.catalogItem.name,
                  )}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The demo's chip tones: only a live grant is coloured. */
const APP_CHIP_TONE: Record<AppStatusKey, 'positive' | 'neutral'> = {
  granted: 'positive',
  requested: 'neutral',
  revoked: 'neutral',
  none: 'neutral',
};

export function PersonAccess({ user, hrOnly }: { user: UserPublic; hrOnly: boolean }) {
  const queryClient = useQueryClient();
  const [showRemoved, setShowRemoved] = useState(false);
  const [dialog, setDialog] = useState<Dialog | null>(null);

  const recordsQuery = useQuery({
    queryKey: queryKeys.accessRecords({ userId: user.id, includeRemoved: showRemoved }),
    queryFn: () => listAccessRecords({ userId: user.id, includeRemoved: showRemoved }),
  });
  // The live set separately, so "Show removed (n)" can say how many there are to show.
  const allRecordsQuery = useQuery({
    queryKey: queryKeys.accessRecords({ userId: user.id, includeRemoved: true }),
    queryFn: () => listAccessRecords({ userId: user.id, includeRemoved: true }),
  });
  const requestQueries = useQueries({
    queries: REQUEST_STATUSES.map((status) => ({
      queryKey: queryKeys.accessRequests({ status, userId: user.id }),
      queryFn: () => listAccessRequests({ status, userId: user.id }),
    })),
  });
  // Revoked grants too, so a revoked app reads as Revoked rather than Not granted.
  const grantsQuery = useQuery({
    queryKey: queryKeys.grantsForUser(user.id, true),
    queryFn: () => listGrants({ userId: user.id, includeRevoked: true }),
    enabled: !hrOnly,
  });
  const applicationsQuery = useApplicationsList(!hrOnly);
  const usersQuery = useQuery({
    queryKey: queryKeys.users,
    queryFn: () => listUsers({ limit: LIST_LIMIT }),
  });
  const names = useMemo(
    () => new Map((usersQuery.data?.data ?? []).map((person) => [person.id, person.name])),
    [usersQuery.data],
  );

  const shown = recordsQuery.data?.data ?? [];
  const live = shown.filter((record) => record.status !== 'removed');
  const removedCount = (allRecordsQuery.data?.data ?? []).filter(
    (record) => record.status === 'removed',
  ).length;
  const attention = live.filter((record) => record.status !== 'active').length;
  const requests: AccessRequestPublic[] = requestQueries
    .flatMap((query) => query.data?.data ?? [])
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const pending = requests.filter((request) => request.status === 'pending').length;
  const groups = groupByTop(live);
  const lineGroups = groupByTop(shown);

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.accessAll });
  }

  const markGranted = useMutation({
    mutationFn: (record: AccessRecordPublic) => updateAccessRecord(record.id, { status: 'active' }),
    onSuccess: (result) => {
      refresh();
      showToast(STR.access.record.granted(result.record.catalogItem.name));
    },
    onError: (error) => {
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });
  const markRemoved = useMutation({
    mutationFn: (record: AccessRecordPublic) => removeAccessRecord(record.id),
    onSuccess: (result) => {
      refresh();
      showToast(STR.access.record.removed(result.record.catalogItem.name), 'warn');
    },
    onError: (error) => {
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });

  function open(record: AccessRecordPublic) {
    setDialog({ kind: 'record', record, edit: false });
  }

  function edit(record: AccessRecordPublic) {
    setDialog({ kind: 'record', record, edit: true });
  }

  const queuePath = `${surfaceById('accessQueue').path}?user=${encodeURIComponent(user.id)}`;
  const addAction = user.active ? (
    <button
      type="button"
      className="tbtn"
      style={quietButtonStyle}
      onClick={() => {
        setDialog({ kind: 'add' });
      }}
    >
      {STR.access.person.addAccess}
    </button>
  ) : undefined;

  if (recordsQuery.isError) {
    return (
      <Card>
        <CardHeader title={STR.access.person.glanceTitle} />
        <EmptyRow>{STR.access.person.loadFailed}</EmptyRow>
      </Card>
    );
  }

  const apps =
    hrOnly || applicationsQuery.data === undefined || grantsQuery.data === undefined
      ? null
      : [...applicationsQuery.data.data].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
        );
  const appIndex = indexAppStatus(
    grantsQuery.data?.data ?? [],
    requests.flatMap((request) =>
      request.status === 'pending' && request.catalogItem.applicationId !== null
        ? [{ userId: user.id, applicationId: request.catalogItem.applicationId }]
        : [],
    ),
  );

  return (
    <>
      <div
        className="tx-subtle"
        style={{ display: 'flex', gap: '6px 18px', flexWrap: 'wrap', alignItems: 'center', fontSize: 12.5 }}
      >
        <span>
          {STR.access.person.facts.outside(live.length)}
          {attention > 0 ? (
            <>
              {', '}
              <b className="tx-warn-strong" style={{ fontWeight: 650 }}>
                {STR.access.person.facts.attention(attention)}
              </b>
            </>
          ) : null}
        </span>
        {pending > 0 ? <span>{STR.access.person.facts.pending(pending)}</span> : null}
        {attention > 0 || pending > 0 ? (
          <Link to={queuePath} className="tbtn" style={{ ...rowActionStyle, textDecoration: 'none' }}>
            {STR.access.person.openInQueue}
          </Link>
        ) : null}
      </div>

      <OnboardingChecklist user={user} requests={requests} />

      <Card>
        <CardHeader
          title={STR.access.person.glanceTitle}
          aside={
            groups.length === 0 ? undefined : (
              <span className="tx-subtle" style={{ fontSize: 12, fontWeight: 500 }}>
                {STR.access.person.glanceAside(
                  live.length,
                  groups.length,
                  groups[0]?.records[0]?.path[0]?.level.name ?? STR.access.person.glanceGroupColumn,
                )}
              </span>
            )
          }
          {...(addAction === undefined ? {} : { action: addAction })}
        />
        <div style={{ padding: '12px 18px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {apps === null ? null : (
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
              <span className="tx-subtle" style={{ fontSize: 11.5, fontWeight: 650 }}>
                {STR.access.person.glanceSuiteApps}
              </span>
              {apps.length === 0 ? (
                <span className="tx-subtle" style={{ fontSize: 11.5 }}>
                  {STR.access.person.glanceNoApps}
                </span>
              ) : (
                apps.map((application) => {
                  const status = appStatusAt(appIndex, user.id, application.id);
                  const word = STR.access.matrix.appStatusWord[status];
                  return (
                    <span
                      key={application.id}
                      title={STR.access.matrix.appCellTitle(user.name, application.name, word)}
                      style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}
                    >
                      <Chip tone={APP_CHIP_TONE[status]} size="sm">
                        {application.name}
                      </Chip>
                      <span className="tx-subtle" style={{ fontSize: 11 }}>
                        {word}
                      </span>
                    </span>
                  );
                })
              )}
            </div>
          )}
          {recordsQuery.isLoading ? (
            <div className="tx-subtle" style={{ fontSize: 12.5 }}>
              {STR.common.loading}
            </div>
          ) : groups.length === 0 ? (
            <div className="tx-subtle" style={{ fontSize: 12.5 }}>
              {user.active ? STR.access.person.glanceEmpty : STR.access.person.glanceEmptyInactive}
            </div>
          ) : (
            <>
              <GlanceGrid groups={groups} onOpen={open} />
              <div
                className="tx-subtle"
                style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 11.5 }}
              >
                {(['active', 'needs_access', 'requested', 'remove_access'] as const).map((status) => (
                  <Chip key={status} tone={RECORD_STATUS_TONE[status]} size="sm">
                    {recordStatusLabel(status)}
                  </Chip>
                ))}
                <span>{hrOnly ? STR.access.person.glanceLegend : STR.access.person.glanceLegendAdmin}</span>
              </div>
            </>
          )}
        </div>
      </Card>

      <FoldCard title={STR.access.person.linesTitle} count={live.length} aside={STR.access.person.linesAside}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '9px 18px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          {removedCount > 0 ? (
            <label className="tx-subtle" style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11.5 }}>
              <input
                type="checkbox"
                checked={showRemoved}
                onChange={(event) => {
                  setShowRemoved(event.target.checked);
                }}
              />
              {STR.access.person.showRemoved(removedCount)}
            </label>
          ) : null}
          {addAction === undefined ? null : <span style={{ marginLeft: 'auto' }}>{addAction}</span>}
        </div>
        {lineGroups.length === 0 ? (
          <EmptyRow>{user.active ? STR.access.person.glanceEmpty : STR.access.person.glanceEmptyInactive}</EmptyRow>
        ) : (
          lineGroups.map((group) => (
            <div key={group.key}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '9px 18px',
                  borderBottom: '1px solid var(--border)',
                  background: 'var(--surface)',
                }}
              >
                <span className="tx-subtle" style={eyebrowStyle}>
                  {group.name}
                </span>
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {group.records.map((record) => {
                  const isLive = record.status !== 'removed';
                  const sub = [
                    record.login === null
                      ? null
                      : STR.access.person.passwordLine(passwordHandlingLabel(record.passwordHandling)),
                    record.caseLogin === null ? null : STR.access.person.caseLogin(record.caseLogin),
                    record.notes,
                  ].filter((part): part is string => part !== null && part !== '');
                  return (
                    <li
                      key={record.id}
                      className="row"
                      {...(isLive ? {} : { 'data-off': '' })}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(150px,1.1fr) minmax(170px,1.3fr) 118px auto',
                        alignItems: 'center',
                        gap: 14,
                        padding: '9px 18px',
                        borderBottom: '1px solid var(--border)',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{shortName(record)}</div>
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', minWidth: 0 }}>
                          {record.login === null ? (
                            <span className="tx-subtle" style={{ fontSize: 12 }}>
                              {STR.access.person.noLoginRecorded}
                            </span>
                          ) : (
                            <span
                              style={{
                                ...monoStyle,
                                fontSize: 12,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {record.login}
                            </span>
                          )}
                          {record.sharedWith.length > 0 ? (
                            <span
                              className="tx-subtle"
                              style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}
                              title={STR.access.person.sharedWithNames(
                                record.sharedWith.map((person) => person.name).join(', '),
                              )}
                            >
                              {STR.access.person.sharedWith(record.sharedWith.length)}
                            </span>
                          ) : null}
                        </div>
                        {sub.length === 0 ? null : (
                          <div className="tx-subtle" style={{ fontSize: 11 }}>
                            {sub.join('; ')}
                          </div>
                        )}
                      </div>
                      <div>
                        <Chip tone={RECORD_STATUS_TONE[record.status]}>
                          {recordStatusLabel(record.status)}
                        </Chip>
                      </div>
                      <div style={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        {isLive && record.status === 'needs_access' ? (
                          <button
                            type="button"
                            className="tbtn"
                            style={rowActionStyle}
                            disabled={markGranted.isPending}
                            onClick={() => {
                              markGranted.mutate(record);
                            }}
                          >
                            {STR.access.person.markGranted}
                          </button>
                        ) : null}
                        {isLive && record.status === 'remove_access' ? (
                          <button
                            type="button"
                            className="tbtn"
                            style={rowActionStyle}
                            disabled={markRemoved.isPending}
                            onClick={() => {
                              markRemoved.mutate(record);
                            }}
                          >
                            {STR.access.person.markRemoved}
                          </button>
                        ) : null}
                        {isLive ? (
                          <button
                            type="button"
                            className="tbtn"
                            style={rowActionStyle}
                            onClick={() => {
                              edit(record);
                            }}
                          >
                            {STR.access.person.edit}
                          </button>
                        ) : null}
                        {/* History is the audit log: administrators only (server-gated). */}
                        {!hrOnly ? (
                          <button
                            type="button"
                            className="tbtn"
                            style={rowActionStyle}
                            onClick={() => {
                              open(record);
                            }}
                          >
                            {STR.access.person.history}
                          </button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </FoldCard>

      <FoldCard
        title={STR.access.person.requestsTitle}
        count={requests.length}
        aside={pending > 0 ? STR.access.person.requestsAsideWaiting : STR.access.person.requestsAsideDecided}
      >
        {requests.length === 0 ? (
          <EmptyRow>{STR.access.person.requestsEmpty}</EmptyRow>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {requests.map((request) => (
              <li
                key={request.id}
                className="row"
                style={{ padding: '11px 18px', borderBottom: '1px solid var(--border)' }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{request.catalogItem.name}</span>
                  <Chip tone={REQUEST_STATUS_TONE[request.status]} size="sm">
                    {requestStatusLabel(request.status)}
                  </Chip>
                  {request.source === 'onboarding' ? (
                    <span className="tx-subtle" style={{ fontSize: 11.5 }}>
                      {STR.access.person.requestFromOnboarding}
                    </span>
                  ) : null}
                </div>
                <div className="tx-subtle" style={{ fontSize: 11.5 }}>
                  {STR.access.person.requestAskedBy(
                    names.get(request.requestedBy) ?? STR.access.person.someone,
                    formatDate(request.createdAt),
                  )}
                  {request.status !== 'pending' && request.decidedBy !== null ? (
                    <>
                      {STR.accessCommon.pathSeparator}
                      {STR.access.person.requestDecided(
                        requestStatusLabel(request.status),
                        names.get(request.decidedBy) ?? STR.access.person.someone,
                        formatDate(request.decidedAt),
                      )}
                    </>
                  ) : null}
                  {request.decisionNote === null ? null : (
                    <>{STR.access.person.requestNote(request.decisionNote)}</>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </FoldCard>

      {dialog?.kind === 'record' ? (
        <RecordDialog
          record={dialog.record}
          startInEdit={dialog.edit}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
      {dialog?.kind === 'add' ? (
        <AddRecordDialog
          userId={user.id}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
    </>
  );
}
